import React, { useState, useEffect, useRef, forwardRef, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import swal from "sweetalert";

import "../../../assets/scss/core/pages/specialty-manage/common.scss";
import "../../../assets/scss/core/pages/specialty-manage/category.scss";

import {
  FormGroup,
  Form,
  Modal,
  ModalBody,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import Switch from 'react-switch';
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Col,
  PaginationComponent,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Button, RSelect
} from "../../../components/Component";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import http from "../../../utils/http-common";

import {
  addCategory,
  updateCategory,
  deleteCategory,
  changeOrder,
  bulkHomeFeatured,
} from "../../../services/CategoryService";

import {
  changeCurrentCategory,
  fetchListAsync,
  fetchAvailableListAsync,
  getCurrentCategory,
  getCategoryList,
  getCategoryListFilter
} from '../../../stores/categorySlice';
import Dropzone from "react-dropzone";
import { uploadFile, deleteFile } from "../../../services/FileService";
import { ADMIN_TIMEZONE_OPTIONS } from "../../../constants/adminTimezoneOptions";

const INLINE_AUTO_SAVE_FIELDS = new Set([
  "isActive",
  "homeFeatured",
  "label",
  "imageName",
]);

function jurisdictionFieldsFromRow(data) {
  const langs = Array.isArray(data.defaultLanguages)
    ? data.defaultLanguages.join(", ")
    : data.defaultLanguages || "";
  return {
    _id: data._id,
    parentId: data.parent ? data.parent._id : "",
    parentLabel: data.parent ? data.parent.label : "",
    label: data.label,
    imageName: data.imageName,
    imageUrl: data.imageUrl,
    isActive: data.isActive,
    homeFeatured: data.homeFeatured === true,
    countryCode: data.countryCode || "",
    dialCode: data.dialCode || "",
    defaultTimezone: data.defaultTimezone || "",
    defaultCurrency: data.defaultCurrency || "",
    currencySymbol: data.currencySymbol || "",
    defaultLanguages: langs,
  };
}

const CategoryList = (props, goToRootCategoryRef) => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [message, setMessage] = useState("");
  const [errMessage, setErrMessage] = useState("");
  const [, setCurrentFile] = useState(undefined);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileInfos] = useState([]);
  const [editRowData, setEditRowData] = useState(null);
  const [editFieldName, setEditFieldName] = useState('');
  const [tcRootId, setTcRootId] = useState('');
  const [jurisdictionRootId, setJurisdictionRootId] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [categoryBreadcrumbList, setCategoryBreadcrumbList] = useState([{
    _id: '',
    label: 'Root'
  }]);
  
  const currentCategory = useSelector(getCurrentCategory);
  const categoryList = useSelector(getCategoryList);
  const categoryListFilter = useSelector(getCategoryListFilter);
  const dispatch = useDispatch();

  const showHomeFeature = Boolean(tcRootId) && String(currentCategory._id) === String(tcRootId);
  const showJurisdiction =
    Boolean(jurisdictionRootId) && String(currentCategory._id) === String(jurisdictionRootId);
  const allHomeFeatured =
    showHomeFeature &&
    categoryList.length > 0 &&
    categoryList.every((r) => r.homeFeatured === true);
  
  const parentCategoryElement = useRef(null);
  const labelInputElement = useRef(null);
  const deepLinkDone = useRef(false);

  const [formData, setFormData] = useState({
    _id: '',
    parentId: '',
    parentLabel: '',
    label: '',
    imageName: '',
    imageUrl: '',
    isActive: true,
    homeFeatured: false,
    countryCode: '',
    dialCode: '',
    defaultTimezone: '',
    defaultCurrency: '',
    currencySymbol: '',
    defaultLanguages: '',
  });
  const [modal, setModal] = useState({
    edit: false,
    add: false,
    view: false,
  });
  
  // Get current list, pagination
  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = categoryList.slice(indexOfFirstItem, indexOfLastItem);

  const onEditSubmit = useCallback((form) => {
    const payload = {
      _id: form._id,
      parentId: form.parentId,
      parentLabel: form.parentLabel,
      label: form.label,
      imageName: form.imageName,
      imageUrl: form.imageUrl,
      isActive: form.isActive,
      countryCode: form.countryCode,
      dialCode: form.dialCode,
      defaultTimezone: form.defaultTimezone,
      defaultCurrency: form.defaultCurrency,
      currencySymbol: form.currencySymbol,
      defaultLanguages: form.defaultLanguages,
    };
    if (showHomeFeature) {
      payload.homeFeatured = form.homeFeatured === true;
    }
    updateCategory(payload)
      .then(() => {
        setMessage('Category updated successfully!');

        setEditRowData(null);
        setEditFieldName('');

        dispatch(fetchListAsync(currentCategory._id));
        dispatch(fetchAvailableListAsync());
      })
      .catch(() => {
        setErrMessage('Could not update category!');
        swal("Opps!", "Could not update category!", "warning").then(r => {});
      });      
  }, [currentCategory._id, dispatch, showHomeFeature]);

  useEffect(() => {
    dispatch(fetchListAsync(currentCategory._id));
    dispatch(fetchAvailableListAsync());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, rootsRes] = await Promise.all([
          http.get('/api/v1/settings').catch(() => ({ data: { data: [] } })),
          http.post('/api/v1/category/list', { parent: '' }),
        ]);
        if (cancelled) return;
        const rows = settingsRes.data?.data || [];
        const byKey = {};
        rows.forEach((r) => {
          if (r?.key) byKey[r.key] = r.value != null ? String(r.value).trim() : '';
        });
        setTcRootId(byKey.tc_specialties_root_category_id || '');
        const roots = Array.isArray(rootsRes.data?.data) ? rootsRes.data.data : [];
        const jur = roots.find((c) => /^Jurisdiction$/i.test(String(c.label || '')));
        setJurisdictionRootId(jur?._id ? String(jur._id) : '');
      } catch {
        /* keep empty roots */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (deepLinkDone.current) return;
    const params = new URLSearchParams(location.search || '');
    const parent = params.get('parent');
    if (!parent) return;
    deepLinkDone.current = true;
    const label = params.get('label') || 'Category';
    dispatch(changeCurrentCategory({ _id: parent, label }));
    dispatch(fetchListAsync(parent));
    setCategoryBreadcrumbList([
      { _id: '', label: 'Root' },
      { _id: parent, label },
    ]);
  }, [location.search, dispatch]);

  useEffect(() => {
    if (!editRowData || !editFieldName || !INLINE_AUTO_SAVE_FIELDS.has(editFieldName)) return;
    if (editFieldName === 'homeFeatured' && !showHomeFeature) return;
    onEditSubmit(formData);
  }, [formData, editRowData, editFieldName, onEditSubmit, showHomeFeature]);

  // Change Page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const { errors, register, handleSubmit, reset } = useForm();

  const openAddNewModal = () => {
    setMessage('');
    setErrMessage('');
    setSelectedFiles([]);
    setEditRowData(null);
    resetForm();
    onCancelImage();
    
    setModal({ add: true });
  }

  // handles ondrop function of dropzone
  const handleDropChange = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    }
  };

  const onRemoveImage = () => {
    if (!selectedFiles || selectedFiles.length === 0)
      return;

    const isBlob = new URL(selectedFiles[0].preview).protocol === 'blob:';
    if (isBlob) {
      setSelectedFiles([]);
    } else {
      swal({
        title: 'Are you sure?',
        text: 'You will not be able to recover this image!',
        icon: 'warning',
        buttons: [
          'No, cancel it!',
          'Yes, I am sure!'
        ],
        dangerMode: true
      })
        .then((isConfirm) => {
          if (isConfirm) {
            deleteFile(editRowData.imageName, (event) => {
            })
              .then((response) => {
                setSelectedFiles([]);

                if (editRowData && editFieldName !== '')
                  setFormData({ ...editRowData, imageName: '', imageUrl: '' });
              })
              .catch(() => {
                setMessage("Could not delete the file!");
                swal("Opps!", "Could not delete the file!", "warning").then(r => {});
              });  
          }
        })
    }
  }

  const onInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* const onChangeCategory = (value) => {
    const category = categoryListFilter.find((each) => each.value === value);
    setFormData({ ...formData, parentId: value, parentLabel: category.label });
  } */

  const onSubmitForm = (form) => {
    onAddSubmit(form);
  }

  const onAddCategory = (imageName, imageUrl) => {
    const payload = {
      parentId: currentCategory._id,
      parentLabel: currentCategory.label,
      label: formData.label,
      imageName: imageName,
      imageUrl: imageUrl,
      isActive: formData.isActive,
      countryCode: showJurisdiction ? formData.countryCode : undefined,
      dialCode: showJurisdiction ? formData.dialCode : undefined,
      defaultTimezone: showJurisdiction ? formData.defaultTimezone : undefined,
      defaultCurrency: showJurisdiction ? formData.defaultCurrency : undefined,
      currencySymbol: showJurisdiction ? formData.currencySymbol : undefined,
      defaultLanguages: showJurisdiction ? formData.defaultLanguages : undefined,
    };
    if (showHomeFeature) {
      payload.homeFeatured = formData.homeFeatured === true;
    }
    addCategory(payload)
      .then(() => {
        setMessage('Category added successfully!');
        swal("Good job!", "Category added successfully!", "success").then(r => {});

        resetForm();

        dispatch(fetchListAsync(currentCategory._id));
        dispatch(fetchAvailableListAsync());
      })
      .catch(() => {
        setTimeout(() => {
          setErrMessage('Could not add category!');
          swal("Opps!", "Could not add category!", "warning").then(r => {});
        }, 2000);
      });
  }

  const onAddSubmit = () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      onAddCategory('', '');
    } else {
      let currentFile = selectedFiles[0];

      uploadFile(currentFile, () => {
      })
        .then((response) => {
          setSelectedFiles(undefined);

          setFormData({ ...formData, imageName: response.data.name, imageUrl: response.data.url });
          
          onAddCategory(response.data.name, response.data.url);
        })
        .catch(() => {
          setMessage("Could not upload the file!");
          swal("Opps!", "Could not upload the file!", "warning").then(r => {});
        });

      setSelectedFiles(undefined);
    }
  };

  const onIsActiveChange = (isActive) => {
    setFormData({ ...formData, isActive: isActive });
  }

  const onRemoveCategory = (id) => {
    swal({
      title: 'Are you sure?',
      text: 'You will not be able to recover this category!',
      icon: 'warning',
      buttons: [
        'No, cancel it!',
        'Yes, I am sure!'
      ],
      dangerMode: true
    })
      .then((isConfirm) => {
        if (isConfirm) {
          deleteCategory(id)
            .then(() => {
              swal("Good job!", "Category deleted successfully!", "success").then(r => {});

              onFormCancel();
              
              dispatch(fetchListAsync(currentCategory._id));
              dispatch(fetchAvailableListAsync());
            })
            .catch((error) => {
              swal("Opps!", "Could not delete category!", "warning").then(r => {});
            });
        }
      })
  };

  const resetForm = () => {
    const empty = {
      _id: '',
      parentId: '',
      parentLabel: '',
      label: '',
      imageName: '',
      imageUrl: '',
      isActive: true,
      homeFeatured: false,
      countryCode: '',
      dialCode: '',
      defaultTimezone: '',
      defaultCurrency: '',
      currencySymbol: '',
      defaultLanguages: '',
    };
    setFormData(empty);
    reset(empty);
  };

  const onFormCancel = () => {
    resetForm();

    setModal({ edit: false, add: false });

    setMessage('');
    setErrMessage('');
  };

  // parentCategoryRenderer is commented out in render (line 767-774), keeping for potential future use
  // eslint-disable-next-line no-unused-vars
  const parentCategoryRenderer = (data, fieldName) => {
    if (editRowData && editRowData._id === data._id && fieldName === editFieldName) {
      setTimeout(() => {
        if (parentCategoryElement && parentCategoryElement.current)
          parentCategoryElement.current.focus();
      }, 100, parentCategoryElement);

      return (
        <div>
          <RSelect
            name="category"
            inputRef={parentCategoryElement}
            options={categoryListFilter}
            className="category"
            placeholder="Category"
            value={{ value: editRowData.parentId, label: editRowData.parentLabel }}
            onChange={(e) => onSaveParentCategory(e.value)}
            onBlur ={() => setEditRowData(null)}
          />
        </div>
      );
    }

    return (
      <div>
        <span>
          <span>{data.parent ? data.parent.label : 'Root'}</span>
        </span>
      </div>
    );
  }

  const onSaveParentCategory = (value) => {
    const category = categoryListFilter.find((each) => each.value === value);

    setFormData({ ...editRowData, parentId: category.value, parentLabel: category.label });
  }

  const labelRenderer = (data, fieldName) => {
    if (editRowData && editRowData._id === data._id && fieldName === editFieldName) {
      setTimeout(() => {
        if (labelInputElement && labelInputElement.current)
          labelInputElement.current.focus();
      }, 100, labelInputElement);

      return (
        <div>
          <input type="text"
                 ref={labelInputElement}
                 className="input-textbox"
                 defaultValue={data[fieldName]}
                 onKeyDown={(e) => onSaveLabel(e)}
                 onBlur ={() => setEditRowData(null)}
          />
        </div>
      );
    }

    return (
      <div>
        <span>
          {data[fieldName]}
        </span>
      </div>
    );
  }

  const onSaveLabel = (e) => {
    if (e.keyCode !== 13)
      return;

    setFormData({ ...editRowData, label: e.target.value });
  }

  const toggleRowActive = (data, isActive) => {
    onEditField(data, 'isActive');
    setFormData({ ...jurisdictionFieldsFromRow(data), isActive: Boolean(isActive) });
  };

  const toggleRowHomeFeatured = (data, homeFeatured) => {
    if (!showHomeFeature) return;
    onEditField(data, 'homeFeatured');
    setFormData({
      ...jurisdictionFieldsFromRow(data),
      homeFeatured: Boolean(homeFeatured),
    });
  };

  const onToggleAllHomeFeatured = (checked) => {
    if (!showHomeFeature || !currentCategory._id) return;
    bulkHomeFeatured({ parentId: currentCategory._id, homeFeatured: checked })
      .then(() => dispatch(fetchListAsync(currentCategory._id)))
      .catch(() => {
        swal('Oops!', 'Could not update Home feature for all rows.', 'warning');
      });
  };

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onShowSubCategories = (catId, catLabel, shouldAddToBreadcrumb) => {
    dispatch(changeCurrentCategory({ _id: catId, label: catLabel }));
    dispatch(fetchListAsync(catId));

    const newCategoryBreadcrumbList = [];
    for (let i = 0; i < categoryBreadcrumbList.length; i++) {
      const each = categoryBreadcrumbList[i];
      newCategoryBreadcrumbList.push({ _id: each._id, label: each.label });
      if (each._id === catId)
        break;
    }

    if (shouldAddToBreadcrumb) {
      newCategoryBreadcrumbList.push({ _id: catId, label: catLabel });
    }
      
    setCategoryBreadcrumbList([...newCategoryBreadcrumbList]);
  }

  const imageRenderer = (data, fieldName) => {
    if (editRowData && editRowData._id === data._id && fieldName === editFieldName) {
      return (
        <div>
          <Dropzone onDrop={(acceptedFiles) => handleDropChange(acceptedFiles)}>
            {({ getRootProps, getInputProps }) => (
              <section>
                <div {...getRootProps()} className="dropzone upload-zone small bg-lighter my-2 dz-clickable">
                  <input {...getInputProps()} />
                  {selectedFiles && selectedFiles.length > 0 && selectedFiles[0].name ? (
                    <div className="dropzone-image-edit-flex-container">
                      <div
                        key={selectedFiles[0].name}
                        className="dz-preview dz-processing dz-image-preview dz-error dz-complete"
                      >
                        <div className="dz-image">
                          <img src={selectedFiles[0].preview} alt="preview" />
                        </div>
                      </div>
                      <div className="image-edit-icons-container">
                        <Icon
                          name="trash"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            onRemoveImage();
                          }}
                        >
                        </Icon>
                        <Icon
                          name="save"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            onSaveImage();
                          }}
                        >
                        </Icon>
                        <Icon
                          name="cross"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            onCancelImage();
                          }}
                        >
                        </Icon>
                      </div>
                    </div>
                  ) : (
                    <div className="dropzone-no-image-edit-prompt-container">
                      "Drag and drop file here, or click to select file"
                      <div className="image-edit-icons-container">
                        <Icon
                          name="cross"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            onCancelImage();
                          }}
                        >
                        </Icon>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </Dropzone>
        </div>
      );
    }

    // Check if imageUrl exists and is not empty
    const imageUrl = data.imageUrl || data.image_url || data.image || '';
    
    return (
      <div>
        {imageUrl ? (
          <img src={imageUrl} alt={data.label || data.name || "Category image"} />
        ) : (
          <span className="text-silent">No image</span>
        )}
      </div>
    );
  }

  const onSaveImage = () => {
    if (!selectedFiles || selectedFiles.length === 0)
      return;

    const isBlob = new URL(selectedFiles[0].preview).protocol === 'blob:';
    if (!isBlob) {
      const curFile = selectedFiles[0];
      setFormData({ ...editRowData, imageName: curFile.imageName, imageUrl: curFile.imageUrl });

      return;
    }

    let currentFile = selectedFiles[0];

    setCurrentFile(currentFile);

    uploadFile(currentFile, (event) => {
    })
      .then((response) => {
        setSelectedFiles(undefined);

        setFormData({ ...editRowData, imageName: response.data.name, imageUrl: response.data.url });
      })
      .catch(() => {
        setMessage("Could not upload the file!");
        swal("Opps!", "Could not upload the file!", "warning").then(r => {});
      });

    setSelectedFiles(undefined);
  }

  const onCancelImage = () => {
    setEditFieldName('');
    setEditRowData(null);

    // dispatch(fetchListAsync(currentCategory._id));
  }

  const onSelectRow = (data) => {
    const fields = jurisdictionFieldsFromRow(data);
    setEditRowData(fields);
    setFormData(fields);
    setEditFieldName("");

    reset({
      ...fields,
    });

    const curFile = {
      name: data.imageName,
      preview: data.imageUrl,
    }
    setSelectedFiles([]);
    setSelectedFiles(current => [...current, curFile]);
  }

  const onEditField = (data, fieldName) => {
    setEditFieldName(fieldName);
    onSelectRow(data);
  }

  const onDecOrder = (id) => {
    const { order: currentOrder } = categoryList.find((each) => each._id === id);
    
    if (!currentOrder || currentOrder === 1)
      return;

    onChangeOrder(id, currentOrder, -1);
  }

  const onIncOrder = (id) => {
    const { order: currentOrder } = categoryList.find((each) => each._id === id);
    
    if (!currentOrder)
      return;

    onChangeOrder(id, currentOrder, 1);
  }

  const onChangeOrder = (id, order, direction) => {
    changeOrder({
      id,
      order,
      direction,
      parent: currentCategory._id
    })
      .then(() => {
        dispatch(fetchListAsync(currentCategory._id));
      })
      .catch(() => {
        setTimeout(() => {
          setErrMessage('Could not change order!');
          swal("Opps!", "Could not change order!", "warning").then(r => {});
        }, 2000);
      });    
  }

  return (
    <React.Fragment>
      <Head title="Category List"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page className="admin-cat-title">
                List of Category
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>
                  {categoryList.length} categories · click a name to edit it inline
                </p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block className="category-block">
          <div className="container-fluid">
            <div className="row">
              <div className="col col-12 col-md-12 col-lg-12">
                <DataTable className="card-stretch" scrollable>
                  <div className="card-inner position-relative card-tools-toggle">
                    <div className="card-title-group">
                      <div>
                        <Breadcrumb>
                        {categoryBreadcrumbList.length > 0
                          ? categoryBreadcrumbList.map((item) => {
                          return (
                            <BreadcrumbItem key={item._id}><button type="button" className="btn btn-link p-0 text-primary" onClick={() => onShowSubCategories(item._id, item.label, false)}>{item.label}</button></BreadcrumbItem>
                          )}) : null}
                        </Breadcrumb>
                        <input type="hidden" ref={goToRootCategoryRef} onClick={() => onShowSubCategories('', '', false)} />
                      </div>
                      <div className="card-tools mr-n1">
                        <ul className="btn-toolbar gx-1">
                          <button className="btn btn-primary" onClick={() => openAddNewModal()}>
                            Add New Category
                          </button>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <DataTableBody compact>
                    <DataTableHead>
                      {/* <DataTableRow className="nk-tb-col-check">
                        <div className="custom-control custom-control-sm custom-checkbox notext">
                          <input
                            type="checkbox"
                            className="custom-control-input form-control"
                            onChange={(e) => selectorCheck(e)}
                            id="uid"
                          />
                          <label className="custom-control-label" htmlFor="uid"></label>
                        </div>
                      </DataTableRow> */}

                      <DataTableRow size="sm">
                        <span className="sub-text"> </span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text">Parent category</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text">ID</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text">Name</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text">Image</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text">Active</span>
                      </DataTableRow>
                      {showHomeFeature ? (
                        <DataTableRow size="lg">
                          <span
                            className="sub-text d-inline-flex align-items-center gap-1"
                            title="Feature on marketing homepage"
                          >
                            Home feature
                            <Switch
                              className="ms-1"
                              height={18}
                              width={32}
                              checked={allHomeFeatured}
                              onChange={onToggleAllHomeFeatured}
                            />
                          </span>
                        </DataTableRow>
                      ) : null}
                      <DataTableRow size="lg">
                        <span className="sub-text">Change order</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text"></span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="sub-text"></span>
                      </DataTableRow>
                    </DataTableHead>
                    {showHomeFeature && currentCategory._id ? (
                      <DataTableItem className="admin-cat-parent-row">
                        <DataTableRow size="sm" dataLabel="">
                          <span className="text-soft">—</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Parent Category">
                          <span className="text-soft">Root</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="ID">
                          <span className="text-soft">parent</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Name">
                          <span>
                            {currentCategory.label}{' '}
                            <span className="badge bg-primary-dim text-primary">parent</span>
                          </span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Image">
                          <span className="text-soft">—</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Active">
                          <span className="text-soft">—</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Home feature">
                          <Switch
                            checked={allHomeFeatured}
                            onChange={onToggleAllHomeFeatured}
                            height={22}
                            width={38}
                            title="Feature whole branch"
                          />
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Change Order">
                          <span className="text-soft">—</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Actions">
                          <span className="text-soft">—</span>
                        </DataTableRow>
                        <DataTableRow size="lg" dataLabel="Subcategories">
                          <span className="text-soft">—</span>
                        </DataTableRow>
                      </DataTableItem>
                    ) : null}
                    {currentItems.length > 0
                      ? currentItems.map((item) => {
                          return (
                            <DataTableItem key={item._id}>
                              <DataTableRow size="sm" dataLabel="">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedIds.has(item._id)}
                                  onChange={() => toggleRowSelected(item._id)}
                                  aria-label={`Select ${item.label || item.uid}`}
                                />
                              </DataTableRow>
                              <DataTableRow size="lg" dataLabel="Parent Category">
                                <span className="text-soft">
                                  {item.parentLabel ? item.parentLabel : 'Root'}
                                </span>
                              </DataTableRow>
                              <DataTableRow size="lg" dataLabel="ID">
                                <span className="text-soft">{item.uid}</span>
                              </DataTableRow>
                              <DataTableRow
                                size="lg"
                                dataLabel="Name"
                                renderer={labelRenderer(item, 'label')}
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  ev.stopPropagation();
                                  onEditField(item, 'label');
                                }}
                              />
                              <DataTableRow
                                size="lg"
                                dataLabel="Image"
                                renderer={imageRenderer(item, 'imageName')}
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  ev.stopPropagation();
                                  onEditField(item, 'imageName');
                                }}
                              />
                              <DataTableRow size="lg" dataLabel="Active">
                                <Switch
                                  checked={item.isActive === true}
                                  onChange={(checked) => toggleRowActive(item, checked)}
                                  height={22}
                                  width={38}
                                />
                              </DataTableRow>
                              {showHomeFeature ? (
                                <DataTableRow size="lg" dataLabel="Home feature">
                                  <Switch
                                    checked={item.homeFeatured === true}
                                    onChange={(checked) => toggleRowHomeFeatured(item, checked)}
                                    height={22}
                                    width={38}
                                  />
                                </DataTableRow>
                              ) : null}
                              <DataTableRow size="lg" dataLabel="Change Order">
                                <div className="change-order-icons-container">
                                  <Icon
                                    name="arrow-up"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      ev.stopPropagation();
                                      onDecOrder(item._id);
                                    }}
                                  >
                                  </Icon>
                                  <Icon
                                    name="arrow-down"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      ev.stopPropagation();
                                      onIncOrder(item._id);
                                    }}
                                  >
                                  </Icon>
                                </div>
                              </DataTableRow>
                              <DataTableRow size="lg" dataLabel="Actions">
                                {showJurisdiction ? (
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="btn-icon me-1"
                                    type="button"
                                    title="Jurisdiction defaults"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      ev.stopPropagation();
                                      onSelectRow(item);
                                    }}
                                  >
                                    <Icon name="map-pin" />
                                  </Button>
                                ) : null}
                                <Icon
                                  name="trash"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                    onRemoveCategory(item._id);
                                  }}
                                >
                                </Icon>
                              </DataTableRow>
                              <DataTableRow size="lg" dataLabel="Subcategories">
                                <div className="view-sub-categories-container">
                                  <Icon
                                    name="arrow-right"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      ev.stopPropagation();
                                      onShowSubCategories(item._id, item.label, true);
                                    }}
                                  >
                                  </Icon>
                                </div>
                              </DataTableRow>
                            </DataTableItem>
                          );
                        })
                      : null}
                  </DataTableBody>
                  <div className="card-inner">
                    {currentItems.length > 0 ? (
                      <PaginationComponent
                        itemPerPage={itemPerPage}
                        totalItems={categoryList.length}
                        paginate={paginate}
                        currentPage={currentPage}
                      />
                    ) : (
                      <div className="text-center">
                        <span className="text-silent">No data found</span>
                      </div>
                    )}
                  </div>
                </DataTable>
              </div>
            </div>
          </div>
        </Block>

        {showJurisdiction && editRowData && editRowData._id ? (
          <Block className="category-block mt-3">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <h5 className="title">Jurisdiction settings (selected row)</h5>
                  <p className="text-soft small">
                    Editing <strong>{editRowData.label}</strong> — optional fields for country defaults.
                  </p>
                  <Form className="row pt-2 gy-3">
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Country code (ISO)</label>
                        <input
                          className="form-control"
                          type="text"
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={(e) => onInputChange(e)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Dial code</label>
                        <input
                          className="form-control"
                          type="text"
                          name="dialCode"
                          value={formData.dialCode}
                          onChange={(e) => onInputChange(e)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Timezone</label>
                        <select
                          className="form-control"
                          name="defaultTimezone"
                          value={formData.defaultTimezone || ""}
                          onChange={(e) => onInputChange(e)}
                        >
                          <option value="">Select timezone…</option>
                          {ADMIN_TIMEZONE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Currency</label>
                        <input
                          className="form-control"
                          type="text"
                          name="defaultCurrency"
                          value={formData.defaultCurrency}
                          onChange={(e) => onInputChange(e)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Currency symbol</label>
                        <input
                          className="form-control"
                          type="text"
                          name="currencySymbol"
                          value={formData.currencySymbol}
                          onChange={(e) => onInputChange(e)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-label">Languages (comma-separated)</label>
                        <input
                          className="form-control"
                          type="text"
                          name="defaultLanguages"
                          value={formData.defaultLanguages}
                          onChange={(e) => onInputChange(e)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <Button
                        color="primary"
                        type="button"
                        onClick={() => onEditSubmit(formData)}
                      >
                        Save jurisdiction fields
                      </Button>
                    </Col>
                  </Form>
                </div>
              </div>
            </div>
          </Block>
        ) : null}

        <Modal isOpen={modal.add} toggle={() => setModal({ add: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Add New Category</h5>
              <div className="mt-4">
                <Form className="row pt-2 gy-4" onSubmit={handleSubmit(onSubmitForm)}>
                  <input type="hidden" value={formData._id} name="_id" />
                  {/* <Col md="12">
                    <FormGroup>
                      <label className="form-label">Category</label>
                      <RSelect
                        name="category"
                        options={categoryListFilter}
                        className="category"
                        placeholder="Category"
                        value={{ value: formData.parentId, label: formData.parentLabel }}
                        onChange={(e) => onChangeCategory(e.value)}
                      />
                      {errors.category && <span className="invalid">{errors.category.message}</span>}
                    </FormGroup>
                  </Col> */}
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Parent Category: {currentCategory && currentCategory.label}</label>
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Name</label>
                      <input
                        className="form-control"
                        type="text"
                        name="label"
                        defaultValue={formData.label}
                        placeholder="Name"
                        ref={register({ required: "This field is required" })}
                        onChange={(e) => onInputChange(e)}
                      />
                      {errors.label && <span className="invalid">{errors.label.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Image</label>
                      <Dropzone onDrop={(acceptedFiles) => handleDropChange(acceptedFiles)}>
                        {({ getRootProps, getInputProps }) => (
                          <section>
                            <div {...getRootProps()} className="dropzone upload-zone small bg-lighter my-2 dz-clickable">
                              <input {...getInputProps()} />
                              {selectedFiles && selectedFiles.length > 0 && selectedFiles[0].name ? (
                                <div>
                                  <div
                                    key={selectedFiles[0].name}
                                    className="dz-preview dz-processing dz-image-preview dz-error dz-complete"
                                  >
                                    <div className="dz-image">
                                      <img src={selectedFiles[0].preview} alt="preview" />
                                    </div>
                                  </div>
                                  <Icon
                                    name="trash"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      ev.stopPropagation();
                                      onRemoveImage();
                                    }}
                                  >
                                  </Icon>
                                </div>
                              ) : (
                                "Drag and drop file here, or click to select file"
                              )}
                            </div>
                          </section>
                        )}
                      </Dropzone>
                      {fileInfos.length > 0 && (
                        <div className="card">
                          <ul className="list-group list-group-flush">
                            {fileInfos.map((file, index) => (
                              <li className="list-group-item" key={index}>
                                <a href={file.url}>{file.name}</a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  {showJurisdiction ? (
                    <>
                      <Col md="12">
                        <hr className="mt-2 mb-2" />
                        <h6 className="title mb-2">Jurisdiction settings</h6>
                        <p className="text-soft small">
                          Country defaults under the Jurisdiction root (ISO code, dial, timezone, currency, languages).
                        </p>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Country code (ISO)</label>
                          <input
                            className="form-control"
                            type="text"
                            name="countryCode"
                            value={formData.countryCode}
                            placeholder="FR"
                            onChange={(e) => onInputChange(e)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Dial code</label>
                          <input
                            className="form-control"
                            type="text"
                            name="dialCode"
                            value={formData.dialCode}
                            placeholder="+33"
                            onChange={(e) => onInputChange(e)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Timezone</label>
                          <select
                            className="form-control"
                            name="defaultTimezone"
                            value={formData.defaultTimezone || ""}
                            onChange={(e) => onInputChange(e)}
                          >
                            <option value="">Select timezone…</option>
                            {ADMIN_TIMEZONE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Currency</label>
                          <input
                            className="form-control"
                            type="text"
                            name="defaultCurrency"
                            value={formData.defaultCurrency}
                            placeholder="EUR"
                            onChange={(e) => onInputChange(e)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Currency symbol</label>
                          <input
                            className="form-control"
                            type="text"
                            name="currencySymbol"
                            value={formData.currencySymbol}
                            placeholder="€"
                            onChange={(e) => onInputChange(e)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="12">
                        <FormGroup>
                          <label className="form-label">Languages (comma-separated)</label>
                          <input
                            className="form-control"
                            type="text"
                            name="defaultLanguages"
                            value={formData.defaultLanguages}
                            placeholder="French, English"
                            onChange={(e) => onInputChange(e)}
                          />
                        </FormGroup>
                      </Col>
                    </>
                  ) : null}
                  <Col md="12">
                    <FormGroup className="is-active-form-group">
                      <label className="form-label is-active-label">Is Active</label>
                      <Switch onChange={onIsActiveChange} checked={formData.isActive} />
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    {message && message !== '' && (
                      <div className="alert alert-light" role="alert">
                        {message}
                      </div>
                    )}
                    {errMessage && errMessage !== '' && (
                      <div className="alert alert-error" role="alert">
                        {errMessage}
                      </div>
                    )}
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit">
                          {formData._id ? 'Edit' : 'Add'} Category
                        </Button>
                      </li>
                      <li>
                        <a
                          href="#cancel"
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </a>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};
export default forwardRef(CategoryList);
